import { createTheme, CSSVariablesResolver } from '@mantine/core';
import i18n from '@xver/i18n';
import { returnFocusFromToast } from '@xver/libs/toastFocus';

export const mantineTheme = createTheme({
    primaryColor: 'blue',
    /** Shade 8 for filled, outline and text colors in both schemes: white on shade 6 is below 4.5:1 (blue 3.56:1, gray 3.32:1). */
    primaryShade: { light: 8, dark: 8 },
    defaultRadius: 'sm',
    fontFamily: 'var(--font-sans)',
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    headings: { fontFamily: 'var(--font-sans)' },

    components: {
        Breadcrumbs: {
            defaultProps: () => ({ role: 'navigation', 'aria-label': i18n.t('common:breadcrumb') }),
        },
        Modal: {
            defaultProps: () => ({
                closeButtonProps: { 'aria-label': i18n.t('common:actions.close') },
            }),
            /** The sticky header must never cover the focused control (SC 2.4.11): focus scrolling stops below it. */
            styles: {
                content: { scrollPaddingTop: '6rem', scrollPaddingBottom: '1rem' },
            },
        },
        Notification: {
            defaultProps: () => ({
                radius: 'md',
                withBorder: true,
                closeButtonProps: {
                    'aria-label': i18n.t('common:actions.close'),
                    onClick: returnFocusFromToast,
                },
            }),
            styles: {
                root: {
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: 'var(--color-border)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
                title: {
                    fontWeight: 700,
                    color: 'var(--color-text-primary)',
                },
                description: {
                    color: 'var(--color-text-secondary)',
                },
                icon: {
                    backgroundColor: 'transparent',
                    color: 'var(--color-text-primary)',
                },
                closeButton: {
                    color: 'var(--color-text-tertiary)',
                },
            },
        },
        Tabs: {
            styles: {
                root: {
                    border: 'none',
                    borderRadius: '8px',
                },
                list: {
                    borderBottom: 'none',
                },
                panel: {
                    borderTop: 'none',
                    padding: '12px',
                },
                tab: {
                    color: 'var(--color-text-primary)',
                },
                tabLabel: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: '15px',
                    gap: '6px',
                },
                tabSection: {
                    display: 'inline-flex',
                    alignItems: 'center',
                },
            },
        },
    },
});

const PLACEHOLDER_COLOR = 'color-mix(in srgb, var(--fg) 68%, transparent)';

/** Keeps Mantine's dimmed text (input descriptions, combobox group labels), input placeholders and input error text at 4.5:1 or more on the app's surfaces. */
export const mantineCssVariablesResolver: CSSVariablesResolver = () => ({
    variables: {},
    light: {
        '--mantine-color-dimmed': 'var(--color-text-secondary)',
        '--mantine-color-placeholder': PLACEHOLDER_COLOR,
        '--mantine-color-error': 'var(--color-error)',
    },
    dark: {
        '--mantine-color-dimmed': 'var(--color-text-secondary)',
        '--mantine-color-placeholder': PLACEHOLDER_COLOR,
        '--mantine-color-error': 'var(--color-error)',
    },
});
